import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../../navbar';
import Header from '../../header';
import { useDispatch, useSelector } from 'react-redux';
import { fetchApplication, updateApplication, updateApplicationData, safePdf, isThunkFulfilled } from '../../../store/reducers/applicationSlice';
import { getSignature, getAttachments } from '../../../store/reducers/fileSlice';
import { setFormFieldData, prepareHtmlForPdf } from '../../../utility/utility';
import FormHeader from '../forms/formHeader/formHeader';
import AddAttachment from './attachments';
import FormLoader from '../forms/formLoader';
import classes from './../appForm/style.module.css';
import { showSuccess, showError } from '../../../helpers/toast';

const AppEdit = () => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const authToken = user.access_token;
    const params = useParams();
    const [searchParams] = useSearchParams();
    const viewOnly = searchParams.get('viewOnly') === 'true';
    const application = useSelector((state) => state.applications.application);
    const [pageIds, setPageIds] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [formIds, setFormIds] = useState([]);
    const [inputFields, setInputFields] = useState({});

    const [isLoaded, setIsLoaded] = useState(false);
    const [signatureIds, setSignatureIds] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [showAttachments, setShowAttachments] = useState(false);
    const [showViewAttachments, setShowViewAttachments] = useState(false);
    const [signatureImage, setSignatureImage] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (dispatch && params.id && authToken) {
            dispatch(fetchApplication({ id: params.id, accessToken: authToken }));
        } else {
            showError('Missing required data for fetchApplication');
        }
    }, [dispatch, params.id, authToken, params]);

    useEffect(() => {

        const runAsyncFunctions = async () => {
            if (isLoaded && user && application && application.json_data && !isSaving) {
                // Set form field values in DOM
                await setFormFieldData(application.json_data);
                // Set inputFields state for saving - this is crucial!
                setInputFields((application.json_data));
                // Use Promise.allSettled to handle both API calls gracefully
                const [signatureResult, attachmentsResult] = await Promise.allSettled([
                    getSignature({ userId: application.user_id, applicationId: params.id, authToken }),
                    getAttachments({ userId: application.user_id, applicationId: params.id, authToken })
                ]);
                // Handle signature result
                if (signatureResult.status === 'fulfilled' && signatureResult.value) {
                    setSignatureImage(signatureResult.value);
                } else {
                    const error = signatureResult.reason;
                    if (error && error.message === 'NOT_FOUND') {
                        showError('No signature found for this application ');
                    } else if (error && error.message === 'SERVER_ERROR') {
                        showError('Server error loading signature (500) - will retry later');
                    } else {
                        showError('Error loading signature:', error?.message || 'Unknown error');
                    }
                }

                // Handle attachments result
                if (attachmentsResult.status === 'fulfilled' && attachmentsResult.value) {
                    const files = attachmentsResult.value.map(filename => ({ name: filename }));
                    setUploadedFiles(files);
                } else {
                    const error = attachmentsResult.reason;
                    setUploadedFiles([]);
                }

            }
        };
        runAsyncFunctions();
    }, [isLoaded, user, application, params.id, authToken, isSaving]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle signature display in form
    useEffect(() => {
        if (signatureImage && signatureIds?.length > 0) {
            signatureIds.forEach(id => {
                const imageInput = document.getElementById(id);
                if (imageInput && imageInput.type === 'image') {
                    const isAuthSig1 = imageInput.getAttribute('data-field-name') === 'AuthSig1';
                    const originalWidth = parseFloat(window.getComputedStyle(imageInput).width) || 0;
                    const height = (originalWidth * 114) / 179;
                    imageInput.style.height = height + "px";
                    // AuthSig1 already has form-defined positioning; do not rewrite `top`
                    if (!isAuthSig1) {
                        const originalTop = parseFloat(window.getComputedStyle(imageInput).top) || 0;
                        imageInput.style.top = (originalTop - height) + 20 + "px";
                    }
                    imageInput.src = signatureImage;
                    imageInput.style.objectFit = 'contain';
                    imageInput.style.pointerEvents = 'none';
                }
            });
        }
    }, [signatureImage, signatureIds]);

    // Lock all form fields when opened in view-only mode
    useEffect(() => {
        if (!viewOnly || !isLoaded) return;

        const lockFormFields = () => {
            const formRoot = document.querySelector(`.${classes['board-content']}`);
            if (!formRoot) return;

            formRoot.querySelectorAll('input, select, textarea').forEach((el) => {
                if (el.type === 'hidden') return;
                el.setAttribute('disabled', 'true');
                el.setAttribute('readonly', 'true');
            });

            formRoot.querySelectorAll('button, [contenteditable="true"]').forEach((el) => {
                el.setAttribute('disabled', 'true');
                el.setAttribute('contenteditable', 'false');
            });
        };

        lockFormFields();
        const timer = setTimeout(lockFormFields, 300);
        return () => clearTimeout(timer);
    }, [viewOnly, isLoaded, currentPage, signatureImage]);

    // Page visibility management - hide all pages and show only current page
    useEffect(() => {
        if (pageIds?.length > 0) {
            // Use requestAnimationFrame to ensure DOM is ready
            requestAnimationFrame(() => {
                // Hide all pages first
                pageIds.forEach(id => {
                    const pageDiv = document.getElementById(id);
                    if (pageDiv) {
                        pageDiv.style.display = "none";
                    }
                });

                // Show only the current page
                const currentPageDiv = document.getElementById(`page${currentPage}`);
                if (currentPageDiv) {
                    currentPageDiv.style.display = "block";
                }
            });
        }
    }, [pageIds, currentPage]);

    // Initialize first page when form loads
    useEffect(() => {
        if (pageIds?.length > 0 && currentPage === 1) {
            // Small delay to ensure DOM is ready
            setTimeout(() => {
                // Show first page when form loads
                const firstPageDiv = document.getElementById('page1');
                if (firstPageDiv) {
                    firstPageDiv.style.display = "block";
                }
            }, 100);
        }
    }, [pageIds, currentPage]);

    // Event handlers for form inputs
    const handleInputChange = useCallback((event) => {
        const { id, value } = event.target;
        setInputFields(prevInputFields => ({
            ...prevInputFields,
            [id]: value,
        }));
    }, []);

    const handleCheckBoxChange = useCallback((event) => {
        const { id, checked, value } = event.target;
        setInputFields(prevInputFields => {
            if (checked) {
                return {
                    ...prevInputFields,
                    [id]: value,
                };
            } else {
                const { [id]: removedValue, ...remainingFields } = prevInputFields;
                return remainingFields;
            }
        });
    }, []);

    const attachEventHandlers = useCallback(() => {

        let tempSignatureIds = [];

        formIds.forEach(id => {
            const inputField = document.getElementById(id);
            if (inputField) {
                if (inputField.type === 'image') {
                    tempSignatureIds.push(id);
                } else if (inputField.type === 'checkbox') {
                    inputField.addEventListener('change', handleCheckBoxChange);
                } else {
                    inputField.addEventListener('change', handleInputChange);
                }
            }
        });
        setSignatureIds(tempSignatureIds);
    }, [formIds, handleInputChange, handleCheckBoxChange]);

    // Attach event handlers when formIds are available
    useEffect(() => {
        if (formIds?.length > 0) {
            attachEventHandlers();
        }
    }, [formIds?.length, attachEventHandlers]);

    const nextPage = () => {
        if (currentPage < pageIds?.length + 1) {
            handlePageChange(currentPage + 1);
        }
    };

    const previousPage = () => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    };

    const hideDiv = (pageNo) => {
        const pageDiv = document.getElementById("page" + pageNo);
        if (pageDiv) pageDiv.style.display = "none";
    };

    const showDiv = (pageNo) => {
        const pageDiv = document.getElementById("page" + pageNo);
        if (pageDiv) pageDiv.style.display = "";
    };

    const handlePageChange = (number) => {
        if (number > 0 && number <= pageIds?.length + 1) {
            if (number === pageIds?.length + 1) {
                // Hide current page and show attachments
                hideDiv(currentPage);
                setCurrentPage(number);
                setShowAttachments(false);
                setShowViewAttachments(true);
                // Debug: Log IDs when opening attachments view
                try {
                    const resolvedUserId = application?.user_id || user?.user?.id;
                    if (process.env.REACT_APP_BACKEND_URL) {
                    }
                } catch (e) {
                }
            } else {
                // Hide attachments and show the requested page
                setShowAttachments(false);
                setShowViewAttachments(false);

                // Hide current page and show new page
                if (currentPage !== pageIds?.length + 1) {
                    hideDiv(currentPage);
                }

                // Small delay to ensure smooth transition
                setTimeout(() => {
                    showDiv(number);
                    setCurrentPage(number);
                }, 50);
            }
        }
    };

    const handleSubmitWithLoading = async (e) => {
        if (viewOnly) return;
        try {
            setIsSaving(true);
            // Update the application data
            const response = await dispatch(updateApplicationData({
                id: params.id,
                json_data: inputFields,
                files: uploadedFiles,
                form: application.name,
                state: 'in_process',
                userId: application.user_id,
                token: authToken
            }));

            if (isThunkFulfilled(response)) {
                // Generate PDF
                let htmlForm = document.getElementById("DocumentPDF").outerHTML;

                dispatch(safePdf({
                    htmlForm: prepareHtmlForPdf(htmlForm, inputFields, pageIds),
                    user: { id: application.user_id },
                    id: params.id,
                    token: authToken
                }));

                showSuccess('Application updated successfully!');
                navigate('/in-process-applications');
            }
        } catch (error) {
            showError('Error updating application. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const saveToDrafts = async (e) => {
        if (viewOnly) return;
        try {
            setIsSaving(true);
            // Update the application state
            const response = await dispatch(updateApplication({
                id: params.id,
                state: e,
                token: authToken
            }));

            if (isThunkFulfilled(response)) {
                // Generate PDF
                let htmlForm = document.getElementById("DocumentPDF").outerHTML;

                dispatch(safePdf({
                    htmlForm: prepareHtmlForPdf(htmlForm, inputFields, pageIds),
                    user_id: user.user.id,
                    id: params.id,
                    token: authToken
                }));

                showSuccess('Application saved to drafts successfully!');
            }
        } catch (error) {
            showError('Error saving to drafts. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveApplicationWithData = async () => {
        if (viewOnly) return;
        try {
            setIsSaving(true);

            // Save application data while preserving the current state
            const response = await dispatch(updateApplicationData({
                id: params.id,
                json_data: inputFields,
                files: uploadedFiles,
                form: application.name,
                state: application?.state || 'in_process', // Preserve current state
                userId: application.user_id || user.user.id,
                token: authToken
            }));

            if (isThunkFulfilled(response)) {
                // Generate PDF
                let htmlForm = document.getElementById("DocumentPDF").outerHTML;
                dispatch(safePdf({
                    htmlForm: prepareHtmlForPdf(htmlForm, inputFields, pageIds),
                    user_id: user.user.id,
                    id: params.id,
                    token: authToken
                }));
                showSuccess('Application saved successfully!');
            }
        } catch (error) {
            showError('Error saving application. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    // Reference to attachments component's saveChanges function
    const attachmentsRef = useRef(null);

    // Function to call saveChanges from attachments component
    const handleSaveChanges = async () => {
        if (attachmentsRef.current && attachmentsRef.current.saveChanges) {

            await attachmentsRef.current.saveChanges();
        }
    };

    return (
        <div>
            <Navbar />
            <Header />
            <div className='wrapper'>
                <div className='boxAppBoard boxWhite'>
                    {application ? (
                        <>
                            <div className="stickyFormHeader">
                                <FormHeader
                                    applicationName={application.name}
                                    nextPage={nextPage}
                                    previousPage={previousPage}
                                    currentPage={currentPage}
                                    totalPages={pageIds?.length + 1}
                                    handleSubmitWithLoading={viewOnly ? undefined : handleSubmitWithLoading}
                                    saveToDrafts={viewOnly ? undefined : saveToDrafts}
                                    handlePageChange={handlePageChange}
                                    applicationId={params.id}
                                    userId={application.user_id || user.user.id}
                                    authToken={authToken}
                                    displayInReview={true}
                                    viewOnly={viewOnly}
                                    applicationState={application.state}
                                    json_data={inputFields}
                                    handleSaveApplication={viewOnly ? undefined : handleSaveApplicationWithData}
                                    pageIds={pageIds}
                                    uploadedFiles={uploadedFiles}
                                    saveChanges={viewOnly ? undefined : handleSaveChanges}
                                    userRole={user.user?.user_role}
                                />

                            </div>

                            <div className={`${classes['board-content']}${viewOnly ? ' form-view-only' : ''}`}>
                                {/* Page indicator */}
                                {/* {pageIds.length > 0 && (
                                    <div style={{
                                        marginBottom: '20px',
                                        padding: '10px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '5px',
                                        textAlign: 'center'
                                    }}>
                                        <span style={{ fontWeight: 'bold', color: '#007bff' }}>
                                            Page {currentPage} of {pageIds.length + 1}
                                            {currentPage === pageIds.length + 1 && ' (Attachments)'}
                                        </span>
                                    </div>
                                )} */}

                                {/* Form content with page navigation styles */}
                                <div style={{ position: 'relative' }}>
                                    <style>
                                        {`
                                            /* Hide all pages by default */
                                            [id^="page"] {
                                                display: none !important;
                                            }
                                            
                                            /* Show only the current page */
                                            #page${currentPage} {
                                                display: block !important;
                                            }
                                            
                                            /* Ensure proper page styling */
                                            [id^="page"] {
                                                width: 100%;
                                                min-height: 400px;
                                            }
                                        `}
                                    </style>
                                    <FormLoader
                                        applicationName={application.name}
                                        setFormIds={setFormIds}
                                        setPageIds={setPageIds}
                                        setIsLoaded={setIsLoaded}
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                            <p>Loading application...</p>
                        </div>
                    )}

                    {application && showAttachments && !viewOnly && (
                        <AddAttachment
                            ref={attachmentsRef}
                            signatureIds={signatureIds}
                            setUploadedFiles={setUploadedFiles}
                            uploadedFiles={uploadedFiles}
                            applicationState={application.state}
                            applicationName={application.name}
                            jsonData={inputFields}
                        />
                    )}

                    {/* Show attachments directly on the page when on attachments page */}
                    {application && showViewAttachments && (
                        <AddAttachment
                            ref={attachmentsRef}
                            applicationId={params.id}
                            userId={application.user_id || user.user.id}
                            signatureIds={signatureIds}
                            setUploadedFiles={setUploadedFiles}
                            uploadedFiles={uploadedFiles}
                            applicationState={application.state}
                            applicationName={application.name}
                            jsonData={inputFields}
                            embedded={true}
                            viewOnly={viewOnly}
                        />
                    )}

                </div>
            </div>
        </div>
    );
}

export default AppEdit